import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import type { MutationObserverOptions } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { useStore } from '@/store/useStore';
import { mergeMutationOptions, mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import type { Domain } from '@/types/models/domain';
import type { Control } from '@/types/models/control';
import type { FrameworkConfig } from '@/types/models/frameworkConfig';
import { SUPPLIER_INFO_QUERY } from '@/modules/org-info/queryKeys';
import {
  SUPPLIER_ASSESSMENT_ANSWERS_QUERY,
  SUPPLIER_ASSESSMENT_OVERRIDES_QUERY,
  SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY,
  SUPPLIER_ASSESSMENT_SCOPING_ANSWERS_QUERY,
  SUPPLIER_COMMUNITY_SIGNALS_QUERY,
  SUPPLIER_COMPLIANCE_QUERY,
  SUPPLIER_COMPLIANCE_VTWO_QUERY,
  SUPPLIER_REASSESSMENT_STATUS_QUERY,
} from '@/modules/profile/queryKeys';
import { SUPPLIERS_QUERY } from '@/modules/connection-view-suppliers/queryKeys';
import { CLIENT_DASHBOARD_QUERY } from '@/modules/client-dashboard/queryKeys';
import { RISKS_QUERY } from '@/modules/risks/queryKeys';
import api from './api';
import {
  COMBINED_FRAMEWORK_CONFIG_QUERY,
  FRAMEWORK_CONFIG_QUERY,
  FRAMEWORK_DEFAULT_CONFIG_QUERY,
  FRAMEWORK_QUERY,
} from './queryKeys';

export function useFramework(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [FRAMEWORK_QUERY],
      async queryFn() {
        const res = await api.getFramework();
        return res.data;
      },
      staleTime: Infinity,
    }),
  );

  // Update the vuex store for legacy code to use
  const store = useStore();
  watchEffect(() => {
    if (store && query.data.value) store.commit('framework/SET_FRAMEWORK', query.data.value);
  });

  const domains = computed(() => query.data.value?.domains ?? []);
  const domainsByID = computed(() =>
    Object.fromEntries(domains.value.map((domain) => [domain.domainID, domain])),
  );

  const sortedDomains = computed<Domain[]>(() =>
    domains.value
      .concat()
      .sort((a, b) => {
        if (a.letter === b.letter) return 0;
        return a.letter > b.letter ? 1 : -1;
      })
      .filter((domain) => !domain.deprecated),
  );

  const addOnDomains = computed(() => domains.value.filter((domain) => domain.isAddOn));

  function domainWithID(domainID: string) {
    const domain = domainsByID.value[domainID];
    if (!domain) throw new Error("Domain doesn't exist");
    return domain;
  }

  const domainsByLetter = computed(() =>
    domains.value.reduce<Record<string, Domain>>((obj, domain) => {
      obj[domain.letter] = domain;
      return obj;
    }, {}),
  );

  const controls = computed(() => query.data.value?.controls ?? []);
  const controlsByID = computed(() =>
    Object.fromEntries(controls.value.map((control) => [control.controlID, control])),
  );

  const sortedControls = computed(() =>
    controls.value
      .concat()
      .sort((a, b) => {
        const x = domainWithID(a.domainID).letter;
        const y = domainWithID(b.domainID).letter;
        if (x === y) return a.number - b.number;
        return x > y ? 1 : -1;
      })
      .filter((control) => !control.deprecated),
  );

  const sortedControlsDeprecated = computed(() =>
    controls.value
      .concat()
      .sort((a, b) => {
        const x = domainWithID(a.domainID).letter;
        const y = domainWithID(b.domainID).letter;
        if (x === y) return a.number - b.number;
        return x > y ? 1 : -1;
      })
      .filter((c) => c.deprecated || domainWithID(c.domainID).deprecated),
  );

  function controlWithID(controlID: string) {
    const control = controlsByID.value[controlID];
    if (!control) throw new Error("Control doesn't exist");
    return control;
  }

  // Does not include deprecated controls
  const controlsByDomain = computed(() =>
    domains.value.reduce<Record<string, string[]>>((obj, domain) => {
      obj[domain.domainID] = sortedControls.value
        .filter((control) => control.domainID === domain.domainID && !control.deprecated)
        .map((control) => control.controlID);
      return obj;
    }, {}),
  );

  const controlsByScanTypeID = computed(() => {
    const pairs = query.data.value?.scanTypeControlPairs || [];
    const controlsByScanType: Record<string, Control[]> = {};

    for (const pair of pairs) {
      if (!controlsByScanType[pair.scanTypeID]) {
        controlsByScanType[pair.scanTypeID] = [];
      }
      const control = controlWithID(pair.controlID);
      controlsByScanType[pair.scanTypeID]!.push(control);
    }

    return controlsByScanType;
  });

  const scanTypeIDsByControlID = computed(() => {
    const pairs = query.data.value?.scanTypeControlPairs || [];
    const scanTypesByControl: Record<string, string[]> = {};

    for (const pair of pairs) {
      if (!scanTypesByControl[pair.controlID]) {
        scanTypesByControl[pair.controlID] = [];
      }
      scanTypesByControl[pair.controlID]!.push(pair.scanTypeID);
    }

    return scanTypesByControl;
  });

  const controlsByDomainDeprecated = computed(() =>
    domains.value.reduce<Record<string, string[]>>((obj, domain) => {
      obj[domain.domainID] = sortedControlsDeprecated.value
        .filter((control) => control.domainID === domain.domainID)
        .map((control) => control.controlID);
      return obj;
    }, {}),
  );

  // Does not include deprecated controls
  const dependentControls = computed(() =>
    sortedControls.value.reduce<Record<string, string[]>>((obj, c) => {
      if (!c.parentID) return obj;
      if (Array.isArray(obj[c.parentID])) obj[c.parentID]!.push(c.controlID);
      else obj[c.parentID] = [c.controlID];
      return obj;
    }, {}),
  );

  // Lookup whether a controlID is a parent of dependent controls or not
  const isParentControl = (controlID: string) =>
    Array.isArray(dependentControls.value[controlID]) &&
    dependentControls.value[controlID].length > 0;

  // Lookup whether a controlID is a dependent control or not
  const isChildControl = (controlID: string) => !!controlWithID(controlID).parentID;

  // Lookup whether a controlID is the last child of its parent
  const isLastChildControl = (controlID: string) => {
    const children = dependentControls.value[controlWithID(controlID).parentID];
    if (!children) return undefined;
    return children[children.length - 1] === controlID;
  };

  const certificationsList = computed(() => [
    ...new Set(
      controls.value.reduce<string[]>((obj, control) => {
        if (control.certifications) obj.push(...control.certifications);
        return obj;
      }, []),
    ),
  ]);

  // Takes controlID as a param returns new object with domain letter & control
  // { id: controlID, name: 'A. 3', domainName: 'Security' }
  const getControlAndDomainName = (controlID: string) => {
    const control = controlWithID(controlID);
    const domain = domainWithID(control.domainID);
    return {
      id: controlID,
      name: `${domain.letter}.${control.number}`,
      domainLetter: domain.letter,
      domainName: domain.name,
    };
  };

  return {
    ...query,
    domains,
    sortedDomains,
    domainWithID,
    domainsByLetter,
    controls,
    sortedControls,
    sortedControlsDeprecated,
    controlWithID,
    controlsByScanTypeID,
    scanTypeIDsByControlID,
    controlsByDomain,
    controlsByDomainDeprecated,
    dependentControls,
    isParentControl,
    isChildControl,
    isLastChildControl,
    certificationsList,
    getControlAndDomainName,
    addOnDomains,
    levels: computed(() => query.data.value?.levels || []),
  };
}

export function useFrameworkDefaultConfig(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [FRAMEWORK_DEFAULT_CONFIG_QUERY],
      async queryFn() {
        const res = await api.getFrameworkDefaultConfig();
        return res.data;
      },
    }),
  );

  return {
    ...query,
    addOnDomains: computed(() => query.data.value?.addOnDomains || []),
    level: computed(() => query.data.value?.level),
  };
}

export function useUpdateFrameworkDefaultConfig(
  options?: MutationObserverOptions<FrameworkConfig, AxiosError, FrameworkConfig, null>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(config) {
        const res = await api.setFrameworkDefaultConfig(config);
        return res.data;
      },
      onSuccess(_data, variables) {
        queryClient.setQueryData(
          [FRAMEWORK_DEFAULT_CONFIG_QUERY],
          (oldData: FrameworkConfig | undefined) => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              ...variables,
            } satisfies FrameworkConfig;
          },
        );
      },
    }),
  );
}

export function useFrameworkConfig(connectionID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [FRAMEWORK_CONFIG_QUERY, toRef(connectionID)] as const,
      async queryFn({ queryKey }) {
        const res = await api.getFrameworkConfig(queryKey[1]);
        return res.data;
      },
    }),
  );

  const level = computed(() => query.data.value?.level);
  const addOnDomains = computed(() => query.data.value?.addOnDomains || []);

  const { sortedControls, sortedDomains, domainWithID } = useFramework();

  // All domain IDs from domains that are required by the framework config.
  const frameworkRequiredDomainIDs = computed(() =>
    sortedDomains.value
      .filter((d) => {
        if (d.isAddOn) {
          return addOnDomains.value.includes(d.domainID);
        }

        if (!level.value) return true;

        // Hide domains with no required controls
        return sortedControls.value.some((control) => {
          if (control.domainID !== d.domainID) return false;
          if (!level.value) return false;
          return control.levels.includes(level.value);
        });
      })
      .map((d) => d.domainID),
  );

  const frameworkRequiredControlIDs = computed(() =>
    sortedControls.value
      .filter((c) => {
        if (domainWithID(c.domainID).isAddOn)
          return frameworkRequiredDomainIDs.value.includes(c.domainID);

        if (!level.value) return true;

        return c.levels.includes(level.value);
      })
      .map((c) => c.controlID),
  );

  return {
    ...query,
    config: query.data,
    level,
    addOnDomains,
    frameworkRequiredDomainIDs,
    frameworkRequiredControlIDs,
  };
}

export function useUpdateFrameworkConfig(
  options?: MutationObserverOptions<
    FrameworkConfig,
    AxiosError,
    { connectionID: string; config: FrameworkConfig; supplierID: string },
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ connectionID, config }) {
        const res = await api.setFrameworkConfig(connectionID, config);
        return res.data;
      },
      async onSuccess(_data, variables) {
        queryClient.setQueryData(
          [FRAMEWORK_CONFIG_QUERY, variables.connectionID],
          (oldData: FrameworkConfig | undefined) => {
            if (!oldData) return undefined;
            return {
              ...oldData,
              ...variables.config,
            } satisfies FrameworkConfig;
          },
        );
        queryClient.invalidateQueries({
          queryKey: [SUPPLIER_ASSESSMENT_ANSWERS_QUERY, variables.supplierID],
        });
        queryClient.invalidateQueries({
          queryKey: [SUPPLIER_ASSESSMENT_SCOPING_ANSWERS_QUERY, variables.supplierID],
        });
        queryClient.invalidateQueries({
          queryKey: [SUPPLIER_COMMUNITY_SIGNALS_QUERY, variables.supplierID],
        });

        // invalidate things that required a delay due to backend eventual consistency
        // This is to work around an eventual consistency issue on the backend
        // In non-backend speak, basically, we have to wait a bit for the
        // backend to catch up with itself due to events and heavy computation.
        await new Promise((resolve) => setTimeout(resolve, 500));
        // This one can be removed once we display overview compliance data from the compliance query.
        queryClient.invalidateQueries({
          queryKey: [SUPPLIER_INFO_QUERY, variables.supplierID],
        });
        queryClient.invalidateQueries({
          queryKey: [SUPPLIER_COMPLIANCE_QUERY, variables.supplierID],
        });
        queryClient.invalidateQueries({
          queryKey: [SUPPLIER_COMPLIANCE_VTWO_QUERY, variables.supplierID],
        });
        queryClient.invalidateQueries({
          queryKey: [SUPPLIER_REASSESSMENT_STATUS_QUERY, variables.supplierID],
        });
        queryClient.invalidateQueries({
          queryKey: [SUPPLIER_ASSESSMENT_OVERRIDES_QUERY, variables.supplierID],
        });
        queryClient.invalidateQueries({
          queryKey: [SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY, variables.supplierID],
        });
        queryClient.invalidateQueries({ queryKey: [SUPPLIERS_QUERY] });
        queryClient.invalidateQueries({ queryKey: [CLIENT_DASHBOARD_QUERY] });
        queryClient.invalidateQueries({ queryKey: [RISKS_QUERY] });
      },
    }),
  );
}

export function useCombinedFrameworkConfig(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [COMBINED_FRAMEWORK_CONFIG_QUERY],
      async queryFn() {
        const res = await api.getCombinedFrameworkConfig();
        return res.data;
      },
    }),
  );

  const addOnDomains = computed(() => query.data.value?.addOnDomains || []);
  const levels = computed(() => query.data.value?.levels || []);

  const { sortedControls, sortedDomains, domainWithID } = useFramework();

  const frameworkRequiredDomains = computed(() =>
    sortedDomains.value.filter((d) => {
      if (d.isAddOn) {
        return addOnDomains.value.includes(d.domainID);
      }

      if (!levels.value.length) return true;

      // Hide domains with no required controls
      return sortedControls.value.some((control) => {
        if (control.domainID !== d.domainID) return false;
        const configLevels = query.data.value?.levels;
        if (!configLevels) return false;
        return control.levels.some((level) => configLevels.includes(level));
      });
    }),
  );
  const frameworkRequiredDomainIDs = computed(() =>
    frameworkRequiredDomains.value.map((d) => d.domainID),
  );
  const frameworkNotRequiredDomains = computed(() =>
    sortedDomains.value.filter((d) => !frameworkRequiredDomainIDs.value.includes(d.domainID)),
  );

  const frameworkRequiredControlIDs = computed(() =>
    sortedControls.value
      .filter((c) => {
        if (domainWithID(c.domainID).isAddOn)
          return frameworkRequiredDomainIDs.value.includes(c.domainID);

        if (!levels.value.length) return true;

        return c.levels.some((level) => levels.value.includes(level));
      })
      .map((c) => c.controlID),
  );

  // Update the vuex store for legacy code to use :(
  const store = useStore();
  watchEffect(() => {
    if (store) {
      store.commit('framework/SET_REQUIRED', {
        requiredDomainIDs: frameworkRequiredDomainIDs.value,
        requiredControlIDs: frameworkRequiredControlIDs.value,
      });
    }
  });

  return {
    ...query,
    query,
    addOnDomains,
    frameworkRequiredDomains,
    frameworkRequiredDomainIDs,
    frameworkNotRequiredDomains,
    frameworkRequiredControlIDs,
  };
}
